import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
} from "@mui/material";
import { useState, useMemo, useEffect } from "react";
import Text from "../../../../../components/Text";
import { useGetPatientBalanceCalculate } from "../../../../../queries/billings/payments/use-get-patient-balance-calculate";
import { useGetSuperBillDetailsByList } from "../../../../../queries/billings/payments/use-get-superbill-details-by-list";
import {
  getErrorSnackbar,
  getFormattedId,
  getSuccessSnackbar,
} from "../../../../../utils/utils";
import { NonNullableDate } from "../../../../../utils/date-time-utility";
import { useBulkUpdateSuperBill } from "../../../../../queries/billings/payments/use-bulk-update-superbill";
import useDebouncedCallback from "../../../../../hooks/use-debounced-callback";
import { useSnackbar } from "../../../../../hooks/useSnackbar";

interface EnterSuperBillsProps {
  onClose: () => void;
  viewOnly?: boolean;
  formik?: any;
  billIds?: any;
  closeAndResetForm?: () => void;
}

const EnterSuperBills = ({
  onClose,
  viewOnly,
  formik,
  billIds,
  closeAndResetForm,
}: EnterSuperBillsProps) => {
  // console.log("billIds", billIds);
  const showSnackbar = useSnackbar();
  const billIdList = billIds
    ? billIds.map((bill: any) => bill.value)
    : formik?.values?.billId?.map((bill: any) => bill.value) || [];
  const { mutateAsync: bulkCalculationOfSuperBill } = useBulkUpdateSuperBill();
  const { data: superBillDetails = [] } =
    useGetSuperBillDetailsByList(billIdList);
  // console.log("superBillDetails", superBillDetails);

  const { mutateAsync: patientBalanceCalculate } =
    useGetPatientBalanceCalculate();

  // State to manage current paid values
  const [currentPaid, setCurrentPaid] = useState<{ [key: string]: string }>({});

  // State to manage patient balance values
  const [patientBalances, setPatientBalances] = useState<{
    [key: string]: string;
  }>({});

  // Calculate total amount entered so far
  const totalEnteredAmount = useMemo(() => {
    return Object.values(currentPaid).reduce((sum, value) => {
      const numValue = Number(value) || 0;
      return sum + numValue;
    }, 0);
  }, [currentPaid]);

  // Calculate remaining amount available
  const remainingAmount = useMemo(() => {
    const totalAmount = formik?.values?.amount || 0;
    return Math.max(0, totalAmount - totalEnteredAmount);
  }, [formik?.values?.amount, totalEnteredAmount]);

  // Update Formik form values when calculated amounts change
  useEffect(() => {
    if (formik) {
      formik.setFieldValue("unappliedAmount", remainingAmount);
      formik.setFieldValue("appliedAmount", totalEnteredAmount);
    }
  }, [remainingAmount, totalEnteredAmount]);

  // Get superbill rows
  const superBillRows = useMemo(() => {
    if (
      superBillDetails &&
      typeof superBillDetails === "object" &&
      !Array.isArray(superBillDetails) &&
      "data" in superBillDetails &&
      Array.isArray((superBillDetails as any).data?.data)
    ) {
      // Convert null values to 0 for numeric fields
      return (superBillDetails as any).data.data.map((row: any) => ({
        ...row,
        patientBalance: row.patientBalance ?? 0,
        prevPaid: row.prevPaid ?? 0,
        total: row.total ?? 0,
        currentPaid: row.currentPaid ?? 0,
      }));
    }
    return [];
  }, [superBillDetails]);

  const handleCurrentPaidChange = (uuid: string, value: string) => {
    const numValue = Number(value) || 0;
    const currentAmount = Number(currentPaid[uuid]) || 0;
    const difference = numValue - currentAmount;

    // Check if the new total would exceed the total amount
    if (totalEnteredAmount + difference > (formik?.values?.amount || 0)) {
      showSnackbar({
        type: "error",
        message: `Total amount cannot exceed ${
          formik?.values?.amount
        }. Remaining amount: ${remainingAmount.toFixed(2)}`,
      });
      return;
    }

    setCurrentPaid((prev) => ({ ...prev, [uuid]: value }));
  };

  const saveBulkCalculationOfSuperBill = () => {
    // console.log("currentPaid", currentPaid);
    // console.log("patientBalances", patientBalances);
    const formattedList = superBillRows
      .map((row: any) => {
        const currentPaidAmount = currentPaid[row.uuid]
          ? Number(currentPaid[row.uuid])
          : 0;
        const existingPrevPaid = row.prevPaid ?? 0;

        return {
          id: row.id,
          superBillId: row.uuid,
          created: row.created,
          patientBalance:
            patientBalances[row.uuid] !== undefined
              ? Number(patientBalances[row.uuid])
              : (row.patientBalance ?? 0),
          prevPaid: existingPrevPaid + currentPaidAmount, // new prevPaid = old prevPaid + currentPaid

          // total: Number(patientBalances[row.uuid]) === 0  ? 0 : row.total - (Number(currentPaid[row.uuid]) ?? 0),
          total: row.total,

          currentPaid: currentPaidAmount > 0 ? currentPaidAmount : undefined, // include entered amount
          superBillStatus:
            Number(patientBalances[row.uuid]) === 0
              ? "PAID"
              : Number(patientBalances[row.uuid]) > 0
                ? "PENDING"
                : "PENDING",
        };
      })
      .filter((item: any) => item.currentPaid !== undefined);

    bulkCalculationOfSuperBill({
      superBillBulk: formattedList,
    })
      .then((res) => {
        showSnackbar(getSuccessSnackbar(res));
      })
      .catch((err) => {
        showSnackbar(getErrorSnackbar(err));
      });
  };

  const handleSave = async () => {
    try {
      // Validate form first
      const errors = await formik.validateForm();
      if (Object.keys(errors).length > 0) {
        // If there are validation errors, submit form to trigger error display
        await formik.submitForm();
        return; // Exit early if validation fails
      }

      // If validation passes, proceed with save
      await formik.submitForm();
      if (superBillRows && superBillRows.length > 0) {
        await saveBulkCalculationOfSuperBill();
      }
      setTimeout(() => {
        closeAndResetForm?.();
      }, 1000);
    } catch (error) {
      console.error("Error saving superbills:", error);
    }
  };

  // Debounced version of CalculatePatientBalance
  const debouncedCalculatePatientBalance = useDebouncedCallback(
    ({
      currentPaid,
      superBillId,
    }: {
      currentPaid: number;
      superBillId: string;
    }) => {
      if (superBillId) {
        patientBalanceCalculate({
          currentPaid: currentPaid,
          superBillId: superBillId,
        })
          .then((data) => {
            setPatientBalances((prev) => ({
              ...prev,
              [superBillId]: String(data.data.data),
            }));
          })
          .catch((err) => {
            showSnackbar(getErrorSnackbar(err));
          });
      }
    },
    500 // 500ms debounce delay
  );

  return (
    <Box width={"100%"} mt={"1rem"}>
      {billIdList.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={"105px"}>
                  <Text>Invoice ID</Text>
                </TableCell>
                <TableCell>
                  <Text>Date</Text>
                </TableCell>
                <TableCell>
                  <Text>Bill Amount</Text>
                </TableCell>
                <TableCell>
                  <Text>Patient Paid</Text>
                </TableCell>
                {!viewOnly && (
                  <TableCell>
                    <Text>Current Paid</Text>
                  </TableCell>
                )}
                <TableCell>
                  <Text>Patient Balance</Text>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {superBillRows.map((row: any) => (
                <TableRow key={row.uuid}>
                  <TableCell>{`${getFormattedId(row.uuid)}`}</TableCell>
                  <TableCell>
                    <Text>{NonNullableDate(row?.created)}</Text>
                  </TableCell>
                  <TableCell>
                    <Text>{row.total}</Text>
                  </TableCell>
                  <TableCell>
                    <Text>{row.prevPaid}</Text>
                  </TableCell>
                  {!viewOnly && (
                    <TableCell>
                      <TextField
                        size="small"
                        value={currentPaid[row.uuid] ?? ""}
                        onChange={(e) => {
                          const inputValue = e.target.value;
                          const numValue = Number(inputValue) || 0;
                          const currentAmount =
                            Number(currentPaid[row.uuid]) || 0;
                          const difference = numValue - currentAmount;

                          // Check if the new total would exceed the total amount
                          if (
                            totalEnteredAmount + difference >
                            (formik?.values?.amount || 0)
                          ) {
                            showSnackbar({
                              type: "error",
                              message: `Total amount cannot exceed ${
                                formik?.values?.amount
                              }. Remaining amount: ${remainingAmount.toFixed(
                                2
                              )}`,
                            });
                            return;
                          }

                          handleCurrentPaidChange(row.uuid, inputValue);
                          debouncedCalculatePatientBalance({
                            currentPaid: numValue,
                            superBillId: row.uuid,
                          });
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    <Text>
                      {patientBalances[row.uuid] ?? row.patientBalance}
                    </Text>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            {!viewOnly && formik?.values?.amount && (
              <TableBody>
                <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableCell colSpan={6} align="right">
                    <Text fontWeight="bold">
                      Amount: ${formik?.values?.amount} | Total Bill Amount: $
                      {totalEnteredAmount.toFixed(2)} | Remaining Amount: $
                      {remainingAmount.toFixed(2)}
                    </Text>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      )}
      {!viewOnly && (
        <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
          <Button variant="contained" onClick={handleSave}>
            Save
          </Button>
          <Button variant="outlined" onClick={onClose}>
            Cancel
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default EnterSuperBills;
